/**
 * transactionalStore.ts — Motor de transacciones locales ACID.
 *
 * QUÉ HACE:
 *  - Permite agrupar varias lecturas/escrituras sobre distintas claves en una
 *    unidad atómica ("todo o nada") mediante `runTransaction`.
 *  - Aísla la operación: la función de la transacción corre en un único turno
 *    del event-loop (sin `await` interno), por lo que ninguna otra operación
 *    puede intercalar lecturas/escrituras a mitad (para una sola instancia).
 *  - Garantiza durabilidad ante interrupciones usando Write-Ahead Log:
 *    stagea todo en memoria → escribe el journal → aplica → limpia el journal.
 *  - Ofrece `txRead`/`txWrite`/`txRemove` para que los helpers existentes
 *    participen en la transacción activa con un cambio mínimo (reemplazar
 *    `getStorage`/`setStorage`). Fuera de una transacción, escriben directo.
 *
 * QUÉ EXPORTA:
 *  - runTransaction: ejecuta una unidad atómica y devuelve su resultado.
 *  - txRead / txWrite / txRemove: acceso que respeta la transacción activa.
 *  - recoverStore: reaplica transacciones incompletas al arrancar (llamar en main).
 *  - isTransactionActive: introspección para tests/depuración.
 *
 * QUÉ NO DEBE HACER:
 *  - No contener lógica de negocio (precios, stock, empleados).
 *  - No importar React, hooks ni componentes.
 *  - No realizar operaciones `await` dentro de la función de transacción
 *    (rompería el aislamiento síncrono). Para I/O async se usará la variante
 *    async del motor en la futura fase híbrida (IndexedDB/nativo).
 */

import { logger } from "@/lib/logger";
import { getActiveBackend, type StorageBackend } from "./storageBackend";
import {
  clearJournal,
  recoverPendingTransactions,
  writeJournal,
  type Journal,
  type JournalWrite,
} from "./writeAheadLog";
import { persistenceMetrics } from "./persistenceMetrics";

/** Marca de una clave staged: valor a escribir o eliminación. */
interface StagedEntry {
  value: unknown;
  remove: boolean;
  /** true si el valor debe persistirse como string escalar crudo (setRaw). */
  raw: boolean;
}

/**
 * Contexto de una transacción en curso. Acumula lecturas (snapshot) y escrituras
 * (staged) en memoria; nada toca el backend hasta `commit()`.
 */
class TransactionContext {
  private readonly staged = new Map<string, StagedEntry>();
  private readonly snapshot = new Map<string, unknown>();

  constructor(private readonly backend: StorageBackend) {}

  /** Lee respetando "read-your-writes": staged → snapshot → backend. */
  read<T>(key: string, defaultValue: T): T {
    const stagedEntry = this.staged.get(key);
    if (stagedEntry) {
      return stagedEntry.remove ? defaultValue : (stagedEntry.value as T);
    }
    if (this.snapshot.has(key)) {
      return this.snapshot.get(key) as T;
    }
    const value = this.backend.get<T>(key, defaultValue);
    this.snapshot.set(key, value);
    return value;
  }

  /** Stagea una escritura (no toca el backend todavía). */
  write<T>(key: string, value: T): void {
    this.staged.set(key, { value, remove: false, raw: false });
  }

  /** Lee un string escalar crudo respetando "read-your-writes". */
  readRaw(key: string): string | null {
    const stagedEntry = this.staged.get(key);
    if (stagedEntry) {
      return stagedEntry.remove ? null : (stagedEntry.value as string);
    }
    if (this.snapshot.has(key)) {
      return this.snapshot.get(key) as string | null;
    }
    const value = this.backend.getRaw(key);
    this.snapshot.set(key, value);
    return value;
  }

  /** Stagea una escritura de string escalar crudo (setRaw en commit). */
  writeRaw(key: string, value: string): void {
    this.staged.set(key, { value, remove: false, raw: true });
  }

  /** Stagea una eliminación (no toca el backend todavía). */
  remove(key: string): void {
    this.staged.set(key, { value: undefined, remove: true, raw: false });
  }

  /**
   * Aplica todas las escrituras de forma atómica y durable:
   *  1. Escribe el journal completo (un único set atómico).
   *  2. Aplica cada clave.
   *  3. Limpia el journal.
   * Si el paso 2 se interrumpe, el journal permite reaplicar al reiniciar.
   */
  commit(): void {
    if (this.staged.size === 0) return;

    const writes: JournalWrite[] = [];
    for (const [key, entry] of this.staged) {
      if (entry.remove) {
        writes.push({ key, value: null, remove: true });
        continue;
      }
      // Serializar UNA vez y reutilizar la cadena para el journal y para la
      // clave: evita el doble JSON.stringify + compresión LZ síncrona por
      // commit (causa de bloqueos de UI al vender/registrar movimientos).
      if (entry.raw) {
        const serialized = String(entry.value);
        writes.push({
          key,
          value: entry.value,
          remove: false,
          raw: true,
          serialized,
        });
      } else {
        const serialized = this.backend.serialize(entry.value);
        writes.push({
          key,
          value: entry.value,
          remove: false,
          raw: false,
          serialized,
        });
      }
    }

    const journal: Journal = {
      txId: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      createdAt: new Date().toISOString(),
      // Journal mínimo: guarda solo las cadenas finales (sin duplicar los
      // objetos `value`). Es suficiente para reaplicar de forma idempotente.
      writes: writes.map((w) =>
        w.remove
          ? { key: w.key, value: null, remove: true }
          : w.raw
            ? {
                key: w.key,
                value: null,
                remove: false,
                raw: true,
                serialized: w.serialized,
              }
            : {
                key: w.key,
                value: null,
                remove: false,
                serialized: w.serialized,
              },
      ),
    };

    writeJournal(this.backend, journal);
    for (const write of writes) {
      if (write.remove) {
        this.backend.remove(write.key);
      } else if (write.serialized != null) {
        if (write.raw) {
          this.backend.setRaw(write.key, write.serialized);
        } else {
          this.backend.setSerialized(write.key, write.serialized, write.value);
        }
      } else if (write.raw) {
        this.backend.setRaw(write.key, String(write.value));
      } else {
        this.backend.set(write.key, write.value);
      }
    }
    clearJournal(this.backend);
  }
}

/** Transacción activa del turno actual (una sola instancia/runtime). */
let activeTx: TransactionContext | null = null;

/**
 * Ejecuta `work` como una transacción atómica. Si `work` lanza antes del commit,
 * ninguna escritura llega al backend (rollback trivial: nada staged se aplicó).
 * Las transacciones anidadas se unen a la transacción padre (un solo commit).
 */
export function runTransaction<T>(work: () => T): T {
  if (activeTx) {
    // Transacción anidada: se une a la padre; el commit lo hace la raíz.
    return work();
  }

  const tx = new TransactionContext(getActiveBackend());
  activeTx = tx;
  const t0 = Date.now();
  try {
    const result = work();
    tx.commit();
    // Instrumentación: registrar commit exitoso (fire-and-forget, no bloquea).
    const staged = (tx as unknown as { staged: Map<string, unknown> }).staged;
    const writeCount = staged instanceof Map ? staged.size : 0;
    if (writeCount > 0) {
      const txId = `tx_${t0}_${Math.random().toString(36).slice(2, 6)}`;
      persistenceMetrics.recordCommit(txId, Date.now() - t0, writeCount);
    }
    return result;
  } finally {
    activeTx = null;
  }
}

/** Lee respetando la transacción activa; fuera de transacción lee del backend. */
export function txRead<T>(key: string, defaultValue: T): T {
  if (activeTx) return activeTx.read(key, defaultValue);
  return getActiveBackend().get<T>(key, defaultValue);
}

/** Escribe stageando en la transacción activa; fuera de ella escribe directo. */
export function txWrite<T>(key: string, value: T): void {
  if (activeTx) {
    activeTx.write(key, value);
    return;
  }
  getActiveBackend().set<T>(key, value);
}

/** Elimina stageando en la transacción activa; fuera de ella elimina directo. */
export function txRemove(key: string): void {
  if (activeTx) {
    activeTx.remove(key);
    return;
  }
  getActiveBackend().remove(key);
}

/**
 * Lee un string escalar crudo (sin JSON) respetando la transacción activa.
 * Para banderas/punteros legados (`caja_last_status`, `altenot_punto_caja_id`)
 * que otros módulos leen con `localStorage.getItem` directo.
 */
export function txReadRaw(key: string): string | null {
  if (activeTx) return activeTx.readRaw(key);
  return getActiveBackend().getRaw(key);
}

/**
 * Escribe un string escalar crudo (sin JSON) stageando en la transacción activa;
 * fuera de ella escribe directo. Conserva el formato exacto en disco que otros
 * módulos esperan leer con `localStorage.getItem`.
 */
export function txWriteRaw(key: string, value: string): void {
  if (activeTx) {
    activeTx.writeRaw(key, value);
    return;
  }
  getActiveBackend().setRaw(key, value);
}

/** true si hay una transacción en curso (para tests/depuración). */
export function isTransactionActive(): boolean {
  return activeTx !== null;
}

/**
 * Reaplica cualquier transacción incompleta pendiente. Debe llamarse una vez al
 * arrancar la app (antes de renderizar), para garantizar durabilidad tras un
 * cierre inesperado.
 *
 * @returns número de escrituras reaplicadas.
 */
export function recoverStore(): number {
  const replayed = recoverPendingTransactions(getActiveBackend());
  if (replayed > 0) {
    persistenceMetrics.recordWalRecover(replayed);
    logger.info(
      `[WAL] Reaplicadas ${replayed} escritura(s) pendientes desde el journal.`,
    );
  }
  return replayed;
}
